export type StickId = "85" | "100" | "114";

export type Variant = {
  // Color is no longer a fixed union (2026-08-23) -- it's admin-managed data (dbo.Colors), matched
  // by whatever Color row id/name the caller currently has selected, not a hardcoded id.
  colorName: string | null;
  stick: StickId;
};

// One real photo per color, in public/images/variants/ -- named by the color's own dbo.Colors.Name,
// lowercased with spaces turned to hyphens ("Jade White" -> jade-white.jpg, "Blue Grey" -> blue-grey.jpg).
// All 30 real Bambu Lab PLA Basic colors happen to slugify this way with no collisions or exceptions
// needed, so a plain transform is enough -- no lookup table to keep in sync as colors are added/renamed.
function colorSlug(colorName: string): string {
  return colorName.toLowerCase().replace(/\s+/g, "-");
}

// Falls back to the old static hero photo only if colorName is missing or a color has no photo yet (e.g.
// a brand-new dbo.Colors row added before its /images/variants/<slug>.jpg exists) -- not a normal-path
// image anymore. In practice this basically never fires: BuyExperience always defaults colorId to
// colors[0]?.id, so a real colorName reaches here almost every render.
const FALLBACK_IMAGE = "/images/hero-mount.jpg";

/**
 * Returns the gallery image set for a given variant (color + stick fitment).
 *
 * Real per-variant imagery, not a placeholder (2026-08-28) -- one photo per color, selected by
 * colorName. `stick` is deliberately unused: the mount's fitment differences aren't visually distinct
 * enough in these renders to warrant a separate photo per fitment (unlike the earlier placeholder's own
 * comment, which imagined that as a future direction) -- only one photo exists per variant right now, so
 * the gallery always returns a single-image array (no multi-angle set yet, see ProductGallery's own
 * handling of a length-1 images array).
 */
export function getVariantImages(variant: Variant): string[] {
  if (!variant.colorName) return [FALLBACK_IMAGE];
  return [`/images/variants/${colorSlug(variant.colorName)}.jpg`];
}
